import {ContributorFooter} from '@github-ui/contributor-footer'
import type {ValidationResult} from '@github-ui/entity-validators'
import {validateComment} from '@github-ui/entity-validators'
import type {CloseButtonState} from '@github-ui/issue-actions'
import {commitCloseIssueMutation, commitReopenIssueMutation, IssueActionPermissions} from '@github-ui/issue-actions'
import type {IssueBodyQuery} from '@github-ui/issue-body'
import {IssueBodyGraphqlQuery} from '@github-ui/issue-body'
import {useItemPickersContext} from '@github-ui/item-picker'
import {
  MarkdownEditor,
  type MarkdownEditorConfig,
  ReadonlyMarkdownEditor,
  type Subject,
} from '@github-ui/markdown-editor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {testIdProps} from '@github-ui/test-id-props'
import {useToastContext} from '@github-ui/toast'
import {useKeyPress} from '@github-ui/use-key-press'
import {LockIcon} from '@primer/octicons-react'
import {Box, Heading, useResizeObserver} from '@primer/react'
import type {MarkdownEditorHandle} from '@primer/react/experimental'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery, useRelayEnvironment} from 'react-relay'
import {ConnectionHandler} from 'relay-runtime'

import {BUTTON_LABELS, CLASS_NAMES, ERRORS, HOTKEYS, LABELS, TEST_IDS, withClassSelector} from '../../constants'
import {IDS} from '../../constants/dom-elements'
import {addCommentMutation} from '../../mutations/add-comment-mutation'
import {type SelectionContext, selectQuoteFromComment} from '../../utils/quotes'
import type {IssueCommentComposer$key} from './__generated__/IssueCommentComposer.graphql'

type IssueCommentComposerBaseProps = {
  onChange: () => void
  onSave: () => void
  onCancel: () => void
  onNewIssueComment?: () => void
  markdownEditorConfig?: MarkdownEditorConfig
  fileUploadsEnabled?: boolean
  singleKeyShortcutEnabled: boolean
}

type IssueCommentComposerProps = IssueCommentComposerBaseProps & {
  issuesQueryRef: PreloadedQuery<IssueBodyQuery>
}

type IssueCommentComposerInternalProps = IssueCommentComposerBaseProps & {
  issue: IssueCommentComposer$key
}

export type IssueCommentComposerRef = {
  appendText: (text: string) => void
  setText: (text: string) => void
  focus: () => void
}

const MAX_CONDENSED_WIDTH = 768

const IssueCommentComposer = forwardRef<IssueCommentComposerRef, IssueCommentComposerProps>(
  ({issuesQueryRef, ...props}, ref) => {
    const preloadedData = usePreloadedQuery<IssueBodyQuery>(IssueBodyGraphqlQuery, issuesQueryRef)
    return preloadedData.repository && preloadedData.repository.issue ? (
      <IssueCommentComposerInternal ref={ref} {...props} issue={preloadedData.repository.issue} />
    ) : null
  },
)
IssueCommentComposer.displayName = 'IssueCommentComposer'
export {IssueCommentComposer}

const IssueCommentComposerInternal = forwardRef<IssueCommentComposerRef, IssueCommentComposerInternalProps>(
  (
    {
      onChange,
      onSave,
      onCancel,
      onNewIssueComment,
      markdownEditorConfig,
      singleKeyShortcutEnabled,
      issue,
      fileUploadsEnabled,
    },
    ref,
  ) => {
    const data = useFragment(
      graphql`
        fragment IssueCommentComposer on Issue {
          id
          locked
          viewerCanComment
          viewerCanClose
          viewerCanReopen
          databaseId
          repository {
            databaseId
            isArchived
            nameWithOwner
            slashCommandsEnabled
            securityPolicyUrl
            contributingFileUrl
            codeOfConductFileUrl
            visibility
          }
          ...IssueActions
        }
      `,
      issue,
    )
    const {id: issueId} = data
    const markdownEditorRef = useRef<MarkdownEditorHandle>(null)
    const commentComposerRef = useRef<HTMLDivElement>(null)
    const repositoryDatabaseId = data.repository.databaseId!
    const isRepoArchived = data.repository.isArchived
    const {addToast} = useToastContext()
    const environment = useRelayEnvironment()
    const [body, setBody] = useState<string>('')
    const [validationResult, setValidationResult] = useState<ValidationResult>(validateComment(body))
    const [metaAndShiftKeysPressed, setMetaAndShiftKeysPressed] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [closeButtonState, setCloseButtonState] = useState<CloseButtonState>('OPEN')
    const [condensed, setCondensed] = useState<boolean>(false)

    useImperativeHandle(
      ref,
      () => ({
        appendText: (text: string) => {
          setBody(current => `${current}\n\n${text}`)
        },
        setText: (text: string) => {
          setBody(text)
        },
        focus: () => {
          markdownEditorRef.current?.focus()
        },
      }),
      [setBody],
    )

    // keep track of when meta and shift keys are pressed
    useEffect(() => {
      // eslint-disable-next-line no-restricted-syntax
      const handleKeyDown = (e: KeyboardEvent) => setMetaAndShiftKeysPressed((e.ctrlKey || e.metaKey) && e.shiftKey)
      const handleKeyUp = () => setMetaAndShiftKeysPressed(false)

      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('keyup', handleKeyUp)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keyup', handleKeyUp)
      }
    }, [markdownEditorRef])

    useEffect(() => setBody(body), [body, setBody])
    useEffect(() => {
      setValidationResult(validateComment(body))
    }, [body])

    const markdownChanged = (newMarkdown: string) => {
      setBody(newMarkdown)
      onChange()
      if (newMarkdown === '') onCancel()
    }

    // clear composer body on issue change
    useEffect(() => {
      setBody('')
    }, [issueId])

    const markdownStyles: BetterSystemStyleObject = useMemo(() => {
      return {
        footer: {
          flexWrap: 'wrap-reverse',

          // styles to hide text on the the default comment composer buttons
          '& > div:first-of-type [data-component="text"]': {
            display: condensed ? 'none' : 'block',
          },

          // styles to wrap custom comment composer buttons
          '& > div:last-of-type': {
            flexWrap: 'wrap',
          },
        },
      }
    }, [condensed])

    // checks for the width of the composer and updates the condensed state accordingly
    const updateCondensedState = () => {
      const clientWidth = commentComposerRef?.current?.clientWidth || MAX_CONDENSED_WIDTH
      setCondensed(clientWidth < MAX_CONDENSED_WIDTH)
    }

    // updates the condensed state when the composer's width is resized
    useResizeObserver(() => {
      updateCondensedState()
    }, commentComposerRef)

    // updates the condensed state on mount
    useEffect(() => {
      updateCondensedState()
    }, [])

    const onCommentSave = useCallback(
      (subject: string) => {
        setIsSubmitting(true)
        if (body?.length > 0) {
          const issueCommentsConnectionId = ConnectionHandler.getConnectionID(
            data.id, // passed as input to the mutation/subscription
            'IssueBacksideTimeline_timelineItems',
          )
          const connectionId = `${issueCommentsConnectionId}(visibleEventsOnly:true)`

          addCommentMutation({
            environment,
            input: {
              subject,
              body,
              connectionId,
            },
            onError: () => {
              setIsSubmitting(false)
              addToast({
                type: 'error',
                message: ERRORS.couldNotComment,
              })
            },
            onCompleted: () => {
              setIsSubmitting(false)
              setBody('')
              onNewIssueComment && onNewIssueComment()
            },
          })
        }

        if (metaAndShiftKeysPressed) {
          if (closeButtonState === 'CLOSED' && data.viewerCanClose) {
            commitCloseIssueMutation({
              environment,
              input: {
                issueId: data?.id,
                newStateReason: 'COMPLETED',
              },
              onError: () =>
                addToast({
                  type: 'error',
                  message: ERRORS.couldNotCloseIssue,
                }),
              onCompleted: onNewIssueComment,
            })
          } else if (closeButtonState === 'NOT_PLANNED' && data.viewerCanClose) {
            commitCloseIssueMutation({
              environment,
              input: {
                issueId: data?.id,
                newStateReason: 'NOT_PLANNED',
              },
              onError: () =>
                addToast({
                  type: 'error',
                  message: ERRORS.couldNotCloseIssue,
                }),
              onCompleted: onNewIssueComment,
            })
          } else if (closeButtonState === 'OPEN' && data.viewerCanReopen) {
            commitReopenIssueMutation({
              environment,
              input: {
                issueId: data?.id,
              },
              onError: () =>
                addToast({
                  type: 'error',
                  message: ERRORS.couldNotCloseIssue,
                }),
              onCompleted: onNewIssueComment,
            })
          }
        }

        onSave()
      },
      [
        body,
        metaAndShiftKeysPressed,
        onSave,
        data.id,
        data.viewerCanClose,
        data.viewerCanReopen,
        environment,
        addToast,
        onNewIssueComment,
        closeButtonState,
      ],
    )

    const {anyItemPickerOpen} = useItemPickersContext()

    const onFocusShortcutActivated = useCallback(
      (e: KeyboardEvent) => {
        if (!singleKeyShortcutEnabled || anyItemPickerOpen()) {
          return
        }
        if (markdownEditorRef && markdownEditorRef.current) {
          e.preventDefault()

          const current = window.getSelection()
          if (current && current.anchorNode) {
            const selectionContext: SelectionContext = {anchorNode: current.anchorNode, range: current.getRangeAt(0)}
            const container = current.anchorNode.parentElement
            const quotedItem =
              container?.closest(withClassSelector(CLASS_NAMES.issueComment)) ??
              container?.closest(withClassSelector(CLASS_NAMES.issueBody))

            // Checking if we can identify the item the selection was made on
            if (quotedItem && quotedItem instanceof HTMLDivElement) {
              const newValue = selectQuoteFromComment(quotedItem, selectionContext)
              setBody(`${newValue}`)
            }
          }

          markdownEditorRef.current.scrollIntoView()
          markdownEditorRef.current.focus()
        }
      },
      [anyItemPickerOpen, singleKeyShortcutEnabled],
    )

    useKeyPress([HOTKEYS.focusIssueCommentComposer], onFocusShortcutActivated, {triggerWhenInputElementHasFocus: false})

    const cantCommentReason = isRepoArchived
      ? LABELS.repoArchived
      : data.locked
      ? LABELS.issueLockedToCollaborators
      : LABELS.canNotCommentOnIssue

    const subject = useMemo<Subject>(() => {
      return {
        type: 'issue_comment',
        id: {
          id: data.id,
          databaseId: data.databaseId!,
        },
        repository: {
          databaseId: repositoryDatabaseId,
          nwo: data.repository.nameWithOwner,
          slashCommandsEnabled: data.repository.slashCommandsEnabled,
        },
      }
    }, [
      data.databaseId,
      data.id,
      data.repository.nameWithOwner,
      data.repository.slashCommandsEnabled,
      repositoryDatabaseId,
    ])

    const handleActionButtonUpdate = useCallback(() => {
      if (validationResult && validationResult.isValid) {
        onCommentSave(issueId)
      }
    }, [issueId, onCommentSave, validationResult])

    const actions = (
      <IssueActionPermissions
        actionRef={data}
        onAction={handleActionButtonUpdate}
        hasComment={body.length > 0}
        buttonSize="medium"
        closeButtonState={closeButtonState}
        setCloseButtonState={setCloseButtonState}
      />
    )

    return (
      <Box
        data-testid={TEST_IDS.commentComposer}
        sx={{
          mb: 2,
          mt: 4,
        }}
        ref={commentComposerRef}
        id={IDS.issueCommentComposer}
      >
        <Heading as="h2" sx={{fontSize: 2, mb: 2}}>
          Add a comment
        </Heading>
        {!data.viewerCanComment ? (
          <ReadonlyMarkdownEditor
            reason={cantCommentReason}
            icon={LockIcon}
            {...testIdProps(TEST_IDS.readonlyMarkdownEditor(TEST_IDS.commentComposer))}
          />
        ) : (
          <MarkdownEditor
            ref={markdownEditorRef}
            label="New comment"
            disabled={isSubmitting}
            subject={subject}
            value={body as SafeHTMLString}
            onChange={markdownChanged}
            actions={actions}
            validationResult={validationResult}
            saveButtonText={BUTTON_LABELS.comment}
            saveButtonTrailingIcon={false}
            onSave={() => onCommentSave(issueId)}
            userSettings={markdownEditorConfig}
            fileUploadsEnabled={fileUploadsEnabled}
            sx={markdownStyles}
            buttonSize="medium"
            {...testIdProps(TEST_IDS.markdownEditor(TEST_IDS.commentComposer))}
          />
        )}
        {data.repository.visibility === 'PUBLIC' && (
          <ContributorFooter
            codeOfConductFileUrl={data.repository.codeOfConductFileUrl ?? undefined}
            securityPolicyUrl={data.repository.securityPolicyUrl ?? undefined}
            contributingFileUrl={data.repository.contributingFileUrl ?? undefined}
          />
        )}
      </Box>
    )
  },
)
IssueCommentComposerInternal.displayName = 'CommentComposerInternal'
export {IssueCommentComposerInternal}
